/**
 * GroupImagesCheckbox - Optional "combine images into one document" control
 * for the audit upload page (bd-37d25). Renders nothing unless every selected
 * file is an image AND there are at least two files - otherwise grouping is
 * either undefined (mixed batch) or meaningless (one file).
 */

import { useTranslation } from "react-i18next";
import { Checkbox } from "@/components/ui/checkbox";
import {
  IMAGE_UPLOAD_ALLOWED_MIME_TYPES,
  isAllowedImageMimeType,
} from "@vardic/shared/utils/document-upload";

export interface GroupImagesCheckboxProps {
  files: File[];
  checked: boolean;
  onChange: (next: boolean) => void;
  disabled?: boolean;
}

/** Pure predicate: true when the file looks like one of the image MIMEs we
 * support. Falls back to the extension when the browser did not assign a
 * MIME type. Exported so the parent can use the same definition to clear
 * the grouping flag when the batch becomes ineligible. */
export function isGroupableImage(file: File): boolean {
  if (file.type && isAllowedImageMimeType(file.type)) {
    return true;
  }
  const ext = file.name.split(".").pop()?.toLowerCase();
  if (!ext) return false;
  return IMAGE_UPLOAD_ALLOWED_MIME_TYPES.some((mime) => mime.endsWith(`/${ext === "jpg" ? "jpeg" : ext}`));
}

/** True when the checkbox should appear. Exported so the Upload page can
 * reset the grouping flag when the batch becomes ineligible (e.g. user
 * removes the last extra image, or drops a PDF into a previously-grouped
 * batch). */
export function canGroupImages(files: File[]): boolean {
  return files.length >= 2 && files.every(isGroupableImage);
}

export function GroupImagesCheckbox({
  files,
  checked,
  onChange,
  disabled = false,
}: GroupImagesCheckboxProps) {
  const { t } = useTranslation(["upload"]);

  if (!canGroupImages(files)) {
    return null;
  }

  return (
    <label
      className="mt-4 flex cursor-pointer items-start gap-3 rounded-[var(--r-lg)] border border-[var(--border)] bg-[var(--muted)] p-4 text-sm text-[var(--foreground)]"
      data-test="group-images-row"
    >
      <Checkbox
        className="mt-0.5"
        checked={checked}
        onCheckedChange={(next) => onChange(next === true)}
        disabled={disabled}
        data-test="group-images-checkbox"
        aria-describedby="group-images-hint"
      />
      <div className="flex-1">
        <span className="font-medium">
          {t("upload:grouping.combineLabel", { count: files.length })}
        </span>
        <p
          id="group-images-hint"
          className="mt-1 text-xs text-[var(--muted-foreground)]"
        >
          {t("upload:grouping.combineHint", { count: files.length })}
        </p>
      </div>
    </label>
  );
}
